/**
 * Pure helpers for the cockpit statistics detail chart. The widget receives
 * pre-aggregated weekly buckets (`{ week, count }[]`) from the unified-api
 * trends endpoint. These helpers map them to `{ x, y }` chart points and apply
 * the range filter — kept pure so they are unit-testable without rendering the
 * component.
 */

export interface WeeklyBucket {
  week: string; // ISO 8601 "YYYY-WNN"
  count: number;
}

export interface ChartPoint {
  x: string; // "YYYY-MM-DD" (Monday of the week)
  y: number;
}

// Number of weekly buckets to show for each range key. The buckets are
// lückenlos pre-filled and chronologically ordered, so we slice from the end of
// the sorted array rather than doing date arithmetic on the week string.
const RANGE_WEEKS: Record<string, number> = {
  '7d': 1,
  '30d': 4,
  all: 12,
};

/**
 * Resolve the Monday of an ISO 8601 "YYYY-WNN" week as a "YYYY-MM-DD" string so
 * the chart's date axis stays parseable (a bare "YYYY-WNN" is not a valid Date).
 */
export function weekStartDateString(week: string): string {
  const match = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!match) return '';
  const year = Number(match[1]);
  const weekNum = Number(match[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const day = jan4.getUTCDay() || 7;
  const mondayOfWeek1 = new Date(Date.UTC(year, 0, 4 - (day - 1)));
  const monday = new Date(
    mondayOfWeek1.getTime() + (weekNum - 1) * 7 * 24 * 60 * 60 * 1000,
  );
  return monday.toISOString().slice(0, 10);
}

/**
 * Filter weekly buckets to the last N weeks for a range key, using the position
 * in the (chronologically sorted, lückenlos) bucket array — not date arithmetic.
 */
export function filterWeeklyBuckets(
  buckets: WeeklyBucket[],
  rangeKey: string,
): WeeklyBucket[] {
  if (!buckets?.length) return [];
  const weeks = RANGE_WEEKS[rangeKey] ?? 12;
  const sorted = [...buckets].sort((a, b) => a.week.localeCompare(b.week));
  return sorted.slice(-weeks);
}

/**
 * Map weekly buckets to chronologically sorted `{ x, y }` chart points.
 * Buckets without a parseable week are dropped.
 */
export function weeklyBucketsToSeries(buckets: WeeklyBucket[]): ChartPoint[] {
  if (!buckets?.length) return [];
  return buckets
    .map((bucket) => {
      const x = weekStartDateString(bucket.week);
      if (!x) return null;
      return { x, y: bucket.count };
    })
    .filter((p): p is ChartPoint => p !== null)
    .sort((a, b) => a.x.localeCompare(b.x));
}
